import React, { Component } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { find } from 'lodash'
import { RootState } from '../../../store'
import inventoryCaseApi, { CaseRollbackConsumeRequest } from '../../../apis/inventoryCase.api'
import { ConfirmParams } from '../../../components/Confirm/Confirm.state'
import confirmService from '../../../services/confirm.service'
import alertService from '../../../services/alert.service'
import printService from '../../../services/print.service'
import { replacePage, replaceLimit, replaceSearch } from './ConsumedCases.slice'
import { paginate } from './ConsumedCases.thunk'
import DataTable from '../../../components/DataTable'
import { TableHorizontalHeader } from '../../../components/TableHorizontal'
import { ActionName } from '../../../components/Actions'
import { i18n } from "../../../i18n/i18n";

interface Props extends ConnectedProps<typeof connector> {}

class ConsumedCases extends Component<Props> {
  headers: TableHorizontalHeader[] = [
    { heading: i18n("Case"), path: 'consumable.code' },
    { heading: i18n("Item"), path: 'consumable.inventory.item.code' },
    { heading: i18n("Description"), path: 'consumable.inventory.item.description' },
    { heading: i18n("Work order"), path: 'workOrder.code' },
    { heading: i18n("Quantity"), path: 'consumedQuantity', badge: 'partialBadge' },
    { heading: i18n("Created at"),
      path: 'createdAt',
      date: true,
      tooltip: ['createdUser.username']
    },
    { heading: i18n("Actions") }
  ]

  constructor (props: Props) {
    super(props)
    this.handleLimitChange = this.handleLimitChange.bind(this)
    this.handleSearch = this.handleSearch.bind(this)
    this.handlePaginate = this.handlePaginate.bind(this)
    this.handleActionClick = this.handleActionClick.bind(this)
  }

  case (id: number) {
    return this.props.readCase(id)
  }

  handleLimitChange (limit: string) {
    this.props.replaceLimit(Number(limit))
  }

  handleSearch (search: string) {
    this.props.replaceSearch(search)
  }

  handlePaginate (page: number) {
    this.props.replacePage(page)
  }

  printCase (caseId: number) {
    inventoryCaseApi.print(caseId, markups => {
      if (!markups) return
      printService.printMarkups(markups)
    })
  }

  handleRollbackConfirm (id: number, caseId: number) {
    const payload: CaseRollbackConsumeRequest = {}
    if (id) payload.consumed = id
    inventoryCaseApi.rollbackConsume(caseId, payload, rollback => {
      if (!rollback) return
      alertService.success('Consume rollback is done')
      this.props.paginate()
    })
  }

  handleActionClick (id: number, action: ActionName) {
    const consumable = this.case(id)
    if (consumable) {
      if (action === 'print') this.printCase(consumable.id)
      else if (action === 'rollback') {
        const confirm: ConfirmParams = {
          id,
          question: i18n('Are you sure to rollback the consume?'),
          confirmText: i18n('Yes'),
          cancelText: i18n('No'),
          onConfirm: () => this.handleRollbackConfirm(id, consumable.id)
        }
        confirmService.push(confirm)
      }
    }
  }

  componentDidMount () {
    this.props.paginate()
  }

  render () {
    return (
      <DataTable
        headers={this.headers}
        data={this.props.data}
        total={this.props.total}
        from={this.props.from}
        to={this.props.to}
        currentPage={this.props.currentPage}
        lastPage={this.props.lastPage}
        limit={String(this.props.limit)}
        search={this.props.search}
        onLimtChange={this.handleLimitChange}
        onSearch={this.handleSearch}
        onPaginate={this.handlePaginate}
        onActionClick={this.handleActionClick} />
    )
  }
}

const mapStateToProps = (state: RootState) => {
  const { result, limit, search } = state.consumedCases
  const { data, total, from, to, currentPage, lastPage } = result
  const readCase = (consumedId: number) => {
    const consumed = find(data, { id: consumedId })
    return (consumed && consumed.consumable) ? consumed.consumable : null
  }
  return { data, total, from, to, currentPage, lastPage, limit, search, readCase }
}

const mapDispatchToProps = {
  paginate,
  replacePage,
  replaceLimit,
  replaceSearch
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ConsumedCases)
